import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import Avatar from "../common/Avatar";
import api from "../../services/api";
import { getAssetUrl } from "../../services/helper";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";

const FollowersModal = ({ isOpen, onClose }) => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [page, setPage] = useState(1);
  const limit = 10;

  // Fetch followers/requests with pagination
  const { data, isLoading, error, isFetching } = useQuery({
    queryKey: ["pending-requests", page, 10],
    queryFn: () => api.fetchPendingRequests(page, 3),
    keepPreviousData: true,
  });

  const requests = data?.data || [];
  const pagination = data?.pagination || {};

  // Accept Follow Mutation
  const acceptFollowMutation = useMutation({
    mutationFn: (userId) => api.acceptFollowRequest(userId),
    onSuccess: () => {
      toast.success("Mentorship request accepted successfully");
      onClose();

      queryClient.invalidateQueries({ queryKey: ["pending-requests"] });
    },
  });
  const acceptJoinRoomMutation = useMutation({
    mutationFn: ({ roomId, userId }) => api.acceptJoinRoomRequest(roomId, userId),
    onSuccess: () => {
      toast.success("Room join request accepted successfully");
      onClose();

      queryClient.invalidateQueries({ queryKey: ["pending-requests"] });
    },
  });

  // Accept Mentorship Mutation
  const acceptMentorshipMutation = useMutation({
    mutationFn: (userId) => api.acceptMentorshipRequest(userId),
    onSuccess: () => {
      toast.success("Follow request accepted successfully");
      onClose();
      queryClient.invalidateQueries({ queryKey: ["pending-requests"] });
    },
  });

  const handleAccept = (request) => {
    if (request.request_type === "mentorship") {
      acceptMentorshipMutation.mutate(request.user_id);
    }
    else if (request.request_type === "follow") {
      acceptFollowMutation.mutate(request.user_id);
    }
    else {
      console.log({ request });
      acceptJoinRoomMutation.mutate({
        roomId: request.room_id,
        userId: request.request_id,
      });
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40" onClick={onClose}>
      <div
        className="bg-white rounded-[16px] w-full max-w-[517px] h-auto max-h-[580px] flex flex-col shadow-2xl overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 pt-4 flex-shrink-0">
          <p className="text-[15px] font-medium tracking-wide text-[#1F3A60] uppercase font-body">
            Followers
          </p>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            ✕
          </button>
        </div>

        {/* List */}
        <div className="overflow-y-auto flex-1 py-3" style={{ scrollbarWidth: "none" }}>
          <style jsx>{`
            div::-webkit-scrollbar { display: none; }
          `}</style>

          {isLoading && !data ? (
            <div className="px-6 py-8 text-center text-gray-500">Loading requests...</div>
          ) : error ? (
            <div className="px-6 py-8 text-center text-red-500">Failed to load requests</div>
          ) : requests.length === 0 ? (
            <div className="px-6 py-12 text-center">
              <div className="mx-auto w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-4">
                👥
              </div>
              <p className="text-[#0F172A] font-medium text-lg">No follow requests</p>
              <p className="text-[#64748B] text-sm mt-1">
                No pending follow or mentorship requests at the moment.
              </p>
            </div>
          ) : (
            requests.map((person) => (
              <div
                key={person.request_type == "room_join" ? `${person.user_id}-${person.request_id}` : person.request_id}
                className="flex items-center gap-3 px-6 py-3 hover:bg-gray-50 transition-colors"
              >
                <Avatar
                  src={getAssetUrl(person.avatar)}
                  alt={person.name}
                  size={54.39}
                  className="flex-shrink-0 rounded-full object-cover"
                  style={{ width: "54.39px", height: "54.39px" }}
                />

                <div className="flex-1 min-w-0">
                  <p className="text-[17px] font-semibold text-[#0F172A] truncate font-body">
                    {person.name} {person.request_type == "room_join" ? `(${person.room_name})` : ''}
                  </p>

                </div>

                <button
                  onClick={() => handleAccept(person)}
                  disabled={
                    acceptFollowMutation.isPending || acceptMentorshipMutation.isPending || acceptJoinRoomMutation.isPending
                  }
                  className={`px-5 py-2 rounded-full text-[13px] font-medium flex-shrink-0 transition-all whitespace-nowrap ${person.request_type === "mentorship"
                    ? "bg-[#FEDC5A] text-[#7A5C00] hover:brightness-95"
                    : "bg-[#1F3A60] text-white hover:bg-[#16304f]"
                    }`}
                >
                  {person.request_type === "mentorship"
                    ? "Accept Mentorship"
                    : person.request_type === "follow"
                      ? "Accept Follow"
                      : "Approve Join Room"
                  }
                </button>
              </div>
            ))
          )}
          {/* Load More Button */}
          {/* {pagination.hasNext && ( */}
          <div className="flex justify-center py-4">
            <button
              onClick={() => navigate(`/pending-requests`)}
              className="px-6 py-2 bg-gray-100 hover:bg-gray-200 rounded-full text-sm font-medium transition-colors disabled:opacity-70"
            >
              View All
            </button>
          </div>
          {/* )} */}
        </div>
      </div>
    </div>
  );
};

export default FollowersModal;